import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Loader2 } from 'lucide-react';
import { useCreateSeed } from '@/hooks/use-seeds';
import { toast } from 'sonner';
import { toToastMessage } from '@/lib/api-error';

interface CreateSeedDialogProps {
  categories: string[];
}

export function CreateSeedDialog({ categories }: CreateSeedDialogProps) {
  const [open, setOpen] = useState(false);
  const mutation = useCreateSeed();

  const [formData, setFormData] = useState({
    problem_category: '',
    seed_number: '',
    title: '',
    description: '',
    estimated_minutes: '3',
    difficulty_level: '1',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(
      {
        problem_category: formData.problem_category,
        seed_number: parseInt(formData.seed_number),
        title: formData.title,
        description: formData.description,
        estimated_minutes: parseInt(formData.estimated_minutes),
        difficulty_level: parseInt(formData.difficulty_level),
      },
      {
        onSuccess: () => {
          toast.success('Seed created successfully');
          setOpen(false);
          setFormData({
            problem_category: '',
            seed_number: '',
            title: '',
            description: '',
            estimated_minutes: '3',
            difficulty_level: '1',
          });
        },
        onError: (err) => {
          toast.error(toToastMessage(err, 'Failed to create seed'));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button />}>
        <Plus className="mr-2 h-4 w-4" />
        Create Seed
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Practice Seed</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={formData.problem_category}
              onValueChange={(v) => setFormData((p) => ({ ...p, problem_category: v ?? '' }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="seed_number">Seed Number</Label>
              <Input
                id="seed_number"
                type="number"
                min={1}
                value={formData.seed_number}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((p) => ({ ...p, seed_number: e.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="difficulty">Difficulty (1-5)</Label>
              <Select
                value={formData.difficulty_level}
                onValueChange={(v) => setFormData((p) => ({ ...p, difficulty_level: v ?? '1' }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData((p) => ({ ...p, title: e.target.value }))
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData((p) => ({ ...p, description: e.target.value }))
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="minutes">Estimated Minutes</Label>
            <Input
              id="minutes"
              type="number"
              min={1}
              max={60}
              value={formData.estimated_minutes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData((p) => ({ ...p, estimated_minutes: e.target.value }))
              }
              required
            />
          </div>

          <Button type="submit" className="w-full" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Seed
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
